import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Rocket, Gem, Sparkles } from 'lucide-react';

interface ActivateBoostDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onBuyAndActivate?: () => void;
  availableBoosts: number;
  gemBalance?: number;
  canBuyBoosts?: boolean;
  isLoading?: boolean;
}

export const ActivateBoostDialog: React.FC<ActivateBoostDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  onBuyAndActivate,
  availableBoosts,
  gemBalance = 0,
  canBuyBoosts = false,
  isLoading = false
}) => {
  const navigate = useNavigate();
  
  const hasBoosts = availableBoosts > 0;
  const hasEnoughGems = gemBalance >= 1;
  const canBuyWithGems = !hasBoosts && canBuyBoosts && hasEnoughGems;
  const showNoOptions = !hasBoosts && (!canBuyBoosts || !hasEnoughGems);

  const handleGoToWheel = () => {
    onOpenChange(false);
    navigate('/main/wheel');
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-gray-900 border-white/10 max-w-sm">
        <AlertDialogHeader>
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-luxury-pinkBright/20 flex items-center justify-center">
            <Rocket className="w-8 h-8 text-luxury-pinkBright" />
          </div>
          <AlertDialogTitle className="text-white text-center">
            Ativar Boost por 24h?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-white/60 text-center">
            Seu perfil aparecerá em destaque para todos os usuários nas próximas 24 horas.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Estado 1: Tem boosts disponíveis */}
        {hasBoosts && (
          <div className="bg-white/5 rounded-lg p-3 my-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/60">Boosts disponíveis</span>
              <span className="text-luxury-pinkBright font-semibold">{availableBoosts}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-white/60">Após ativação</span>
              <span className="text-white font-semibold">{Math.max(0, availableBoosts - 1)}</span>
            </div>
          </div>
        )}

        {/* Estado 2: Sem boosts mas pode comprar com gemas */}
        {canBuyWithGems && (
          <div className="bg-white/5 rounded-lg p-3 my-2 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/60">Boosts disponíveis</span>
              <span className="text-white/40 font-semibold">0</span>
            </div>
            <div className="border-t border-white/10 pt-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-white/60 flex items-center gap-1">
                  <Gem className="w-3.5 h-3.5 text-purple-400" />
                  Gemas disponíveis
                </span>
                <span className="text-purple-400 font-semibold">{gemBalance}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-white/60">Custo do boost</span>
                <span className="text-white font-semibold flex items-center gap-1">
                  1 <Gem className="w-3 h-3 text-purple-400" />
                </span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-white/60">Após compra</span>
                <span className="text-purple-400 font-semibold">{gemBalance - 1} 💎</span>
              </div>
            </div>
          </div>
        )}

        {/* Estado 3: Sem boosts e sem gemas */}
        {showNoOptions && (
          <div className="bg-white/5 rounded-lg p-4 my-2 text-center">
            <Sparkles className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
            <p className="text-white/80 text-sm font-medium">
              Você não tem boosts ou gemas
            </p>
            <p className="text-white/50 text-xs mt-1">
              Ganhe gemas girando a Roleta da Sorte!
            </p>
          </div>
        )}

        <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
          {/* Botão principal baseado no estado */}
          {hasBoosts ? (
            <AlertDialogAction
              onClick={onConfirm}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-[#C4406B] to-[#9F3764] hover:opacity-90 text-white"
            >
              {isLoading ? 'Ativando...' : 'Ativar Boost 🚀'}
            </AlertDialogAction>
          ) : canBuyWithGems ? (
            <AlertDialogAction
              onClick={onBuyAndActivate}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:opacity-90 text-white"
            >
              {isLoading ? 'Comprando...' : (
                <span className="flex items-center justify-center gap-2">
                  Comprar e Ativar
                  <span className="flex items-center gap-0.5">
                    (1 <Gem className="w-3.5 h-3.5" />)
                  </span>
                </span>
              )}
            </AlertDialogAction>
          ) : (
            <Button
              variant="gem"
              onClick={handleGoToWheel}
              className="w-full"
            >
              Ir para Roleta da Sorte 🎰
            </Button>
          )}
          
          <AlertDialogCancel className="w-full bg-white/5 border-white/10 text-white hover:bg-white/10">
            Cancelar
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
